import Phaser from 'phaser';
import { GAME_CONSTANTS } from '../config/balance';
import { CubeRenderer } from './CubeRenderer';
import { TrailRenderer } from './TrailRenderer';
import { ParticleManager } from './ParticleManager';
import { TerritoryManager } from './TerritoryManager';
import { MobileControls } from './MobileControls';
import { PlayerEffect } from './PlayerEffect';
import { ItemType } from '../config/items';
import { getTextResolution } from '../utils/display';

export class Player extends Phaser.GameObjects.Container {
  public id: string;
  // 리스폰 무적(피격 후 잠깐) — 아이템 무적과 별개
  private respawnInvincible: boolean = false;
  // 활성 아이템 효과
  public readonly effect = new PlayerEffect();
  // 직전 프레임 역방향 여부 (해제 시 급반전 보정용)
  private wasReversed: boolean = false;

  /** 외부 충돌 판정용: 리스폰 무적 또는 아이템 무적이면 true */
  get isInvincible(): boolean {
    return this.respawnInvincible || this.effect.isInvincible();
  }

  private cubeRenderer: CubeRenderer;
  private trailRenderer: TrailRenderer;
  private particleManager?: ParticleManager;
  private territoryManager?: TerritoryManager;

  private speed: number;
  private direction: number = 0;
  private isBoosting: boolean = false;
  private size: number;
  private color: number;

  private nicknameText: Phaser.GameObjects.Text;
  private effectTimerText: Phaser.GameObjects.Text;
  private isLocal: boolean;
  private nickname: string;
  private hasCrown: boolean = false;

  // 꼬리 관련
  private isOutsideTerritory: boolean = false;
  private trailActive: boolean = false;
  public trail: { x: number; y: number }[] = [];
  private lastInsidePosition: { x: number; y: number } | null = null;

  // 사망 콜백
  private onDeathCallback?: () => void;

  // 영역 완료 콜백 (트레일 포인트 전달)
  private onTerritoryCompleteCallback?: (trailPoints: { x: number; y: number }[]) => void;

  // 맵 경계
  private worldCenterX: number = 1600;
  private worldCenterY: number = 1600;
  private worldRadius: number = 1500;

  // 모바일 컨트롤
  private mobileControls?: MobileControls;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    color: number,
    id: string,
    nickname: string,
    isLocal: boolean = false
  ) {
    super(scene, x, y);
    this.id = id;
    this.isLocal = isLocal;
    this.color = color;
    this.size = GAME_CONSTANTS.map.tileSize;
    this.speed = GAME_CONSTANTS.movement.baseSpeed * 1.2; // 기본 속도 20% 증가

    scene.add.existing(this);
    this.setDepth(10);

    // 큐브 렌더러
    this.cubeRenderer = new CubeRenderer(scene, this.size, color);
    this.cubeRenderer.setDepth(10);

    // 트레일 렌더러 (큐브 면과 동일한 너비)
    const trailWidth = this.cubeRenderer.getBaseWidth();
    this.trailRenderer = new TrailRenderer(scene, trailWidth, color);

    // 닉네임 (큐브 위에 떠있게, 영역보다 위에 표시)
    this.nickname = nickname;
    const cubeHeight = this.cubeRenderer.getTotalHeight();
    this.nicknameText = scene.add.text(0, -cubeHeight - 20, nickname, {
      fontSize: '18px',
      color: '#FFFFFF',
      stroke: '#000000',
      strokeThickness: 4,
      fontFamily: 'Nunito',
      fontStyle: 'bold',
      resolution: getTextResolution(),
    }).setOrigin(0.5);
    this.nicknameText.setDepth(15); // 영역(2), 큐브(10)보다 위

    // 효과 남은시간 카운터 (닉네임 위)
    this.effectTimerText = scene.add.text(0, 0, '', {
      fontSize: '16px',
      color: '#FFE14E',
      stroke: '#000000',
      strokeThickness: 4,
      fontFamily: 'Nunito',
      fontStyle: 'bold',
      resolution: getTextResolution(),
    }).setOrigin(0.5).setDepth(16).setVisible(false);

    if (isLocal) {
      this.setupMouseControls();
    }
  }

  private setupMouseControls(): void {
    const isMobile = (window as any).isMobileDevice || false;

    if (isMobile) {
      // 모바일/태블릿: 가상 조이스틱 + 부스트 버튼 사용
      this.mobileControls = new MobileControls(this.scene);

      this.mobileControls.setOnDirectionChange((direction) => {
        this.direction = direction;
        this.cubeRenderer.setDirection(direction);
      });

      this.mobileControls.setOnBoostChange((isBoosting) => {
        this.isBoosting = isBoosting;
      });
    } else {
      // 데스크톱: 마우스 컨트롤
      this.scene.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
        this.direction = Phaser.Math.Angle.Between(this.x, this.y, pointer.worldX, pointer.worldY);
        this.cubeRenderer.setDirection(this.direction);
      });

      this.scene.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
        if (pointer.leftButtonDown()) {
          this.isBoosting = true;
        }
      });

      this.scene.input.on('pointerup', () => {
        this.isBoosting = false;
      });
    }
  }

  setTerritoryManager(tm: TerritoryManager): void {
    this.territoryManager = tm;
  }

  setParticleManager(pm: ParticleManager): void {
    this.particleManager = pm;
  }

  setWorldBounds(centerX: number, centerY: number, radius: number): void {
    this.worldCenterX = centerX;
    this.worldCenterY = centerY;
    this.worldRadius = radius;
  }

  setOnDeathCallback(callback: () => void): void {
    this.onDeathCallback = callback;
  }

  setOnTerritoryCompleteCallback(callback: (trailPoints: { x: number; y: number }[]) => void): void {
    this.onTerritoryCompleteCallback = callback;
  }

  update(delta: number): void {
    if (this.isLocal) {
      this.updateLocalPlayer(delta);
    }

    // 큐브 렌더링
    this.cubeRenderer.render(this.x, this.y);

    // 닉네임 위치 (큐브 위에 떠있게)
    const cubeHeight = this.cubeRenderer.getTotalHeight();
    this.nicknameText.setPosition(this.x, this.y - cubeHeight - 20);

    // 효과 남은시간 카운터 (닉네임 위)
    const remMs = this.effect.remainingMs(Date.now());
    if (remMs > 0) {
      this.effectTimerText.setText(`⏱ ${Math.ceil(remMs / 1000)}`);
      this.effectTimerText.setPosition(this.x, this.y - cubeHeight - 44);
      this.effectTimerText.setVisible(true);
    } else {
      this.effectTimerText.setVisible(false);
    }
  }

  private updateLocalPlayer(delta: number): void {
    if (!this.territoryManager) {
      return;
    }

    // 아이템 효과 갱신 + 시각 반영
    const now = Date.now();
    this.effect.update(now);
    this.applyEffectVisuals();

    // 역방향 해제 시 급반전(→자기 꼬리 즉사) 방지:
    // 해제 순간 진행 방향을 그대로 유지하도록 입력 방향을 반전시킨다.
    // (머리가 휙 돌지 않고 가던 방향으로 계속 → 사용자가 마우스로 새 방향을 줄 때까지 안전)
    const isReversed = this.effect.isReversed();
    if (this.wasReversed && !isReversed) {
      this.direction += Math.PI;
      this.cubeRenderer.setDirection(this.direction);
    }
    this.wasReversed = isReversed;

    // 부스트 가능 여부 확인 및 영역 소모
    const canBoost = this.isBoosting && this.territoryManager.canBoost(this.id);
    if (canBoost) {
      this.territoryManager.consumeTerritory(this.id, delta);
      // 부스터 꽃가루 효과
      this.particleManager?.boostTrail(this.x, this.y, this.direction, this.color);
    }

    // 역방향 효과: 조작한 방향의 반대로 이동(머리도 이동방향을 향함)
    const moveDir = isReversed ? this.direction + Math.PI : this.direction;
    if (isReversed) {
      this.cubeRenderer.setDirection(moveDir);
    }

    // 이동 (부스트 ×1.5, 아이템 속도 배율 적용)
    const currentSpeed =
      (canBoost ? this.speed * 1.5 : this.speed) * this.effect.speedMultiplier();
    const moveX = Math.cos(moveDir) * currentSpeed * (delta / 1000);
    const moveY = Math.sin(moveDir) * currentSpeed * (delta / 1000);

    let newX = this.x + moveX;
    let newY = this.y + moveY;

    // 원형 맵 경계 체크
    const dist = Phaser.Math.Distance.Between(newX, newY, this.worldCenterX, this.worldCenterY);
    if (dist > this.worldRadius - 30) {
      const angle = Math.atan2(newY - this.worldCenterY, newX - this.worldCenterX);
      newX = this.worldCenterX + Math.cos(angle) * (this.worldRadius - 30);
      newY = this.worldCenterY + Math.sin(angle) * (this.worldRadius - 30);
    }

    this.x = newX;
    this.y = newY;

    // 자기 꼬리 충돌 체크 (영역 밖에서만)
    if (this.trailActive && this.checkSelfTrailCollision()) {
      this.onDeathCallback?.();
      return;
    }

    // 영역 체크
    const wasOutside = this.isOutsideTerritory;
    this.isOutsideTerritory = !this.territoryManager.isInsideOwnTerritory(this.id, this.x, this.y);

    // 영역 안에 있을 때 마지막 위치 저장
    if (!this.isOutsideTerritory) {
      this.lastInsidePosition = { x: this.x, y: this.y };
    }

    // 영역 밖으로 나갔을 때 -> 꼬리 시작
    if (!wasOutside && this.isOutsideTerritory) {
      this.trailActive = true;
      this.trail = [];
      this.trailRenderer.clear();
      // 꼬리 뿌리 저장 (마지막 영역 안 위치 사용)
      if (this.lastInsidePosition) {
        this.territoryManager.setTrailRoot(this.id, this.lastInsidePosition.x, this.lastInsidePosition.y);
      }
    }

    // 꼬리 활성화 상태에서 트레일 추가
    if (this.trailActive && this.isOutsideTerritory) {
      this.trailRenderer.addPoint(this.x, this.y, this.direction);
      this.updateGridTrail();

      // 적 영역 위를 지나갈 때 파편 효과
      const enemyColor = this.territoryManager.getTerritoryColorAt(this.x, this.y);
      if (enemyColor !== null && this.particleManager) {
        this.particleManager.debrisSpray(this.x, this.y, this.direction, enemyColor);
      }
    }

    // 영역 안으로 돌아왔을 때 -> 영역 확장
    if (wasOutside && !this.isOutsideTerritory && this.trailActive) {
      this.completeTerritory();
    }
  }

  private updateGridTrail(): void {
    const { tileSize } = GAME_CONSTANTS.map;
    const gridX = Math.floor(this.x / tileSize);
    const gridY = Math.floor(this.y / tileSize);

    const lastGrid = this.trail.length > 0 ? this.trail[this.trail.length - 1] : null;

    if (!lastGrid || lastGrid.x !== gridX || lastGrid.y !== gridY) {
      this.trail.push({ x: gridX, y: gridY });
    }
  }

  /**
   * 자기 꼬리와 충돌했는지 확인
   * 트레일의 선분들과 현재 위치 사이의 거리를 체크
   */
  private checkSelfTrailCollision(): boolean {
    if (this.isInvincible) return false; // 무적(리스폰/아이템): 자기 꼬리 교차 무시
    const trailPoints = this.trailRenderer.getPoints();
    if (trailPoints.length < 15) return false; // 최소 트레일 길이 증가 (10 -> 15)

    const collisionRadius = this.size * 0.2; // 충돌 반경 감소 (0.3 -> 0.2)

    // 최근 포인트는 제외 (바로 뒤는 항상 닿아있으니까)
    const checkUpTo = trailPoints.length - 8; // 제외 포인트 증가 (5 -> 8)

    for (let i = 0; i < checkUpTo - 1; i++) {
      const p1 = trailPoints[i];
      const p2 = trailPoints[i + 1];

      // 현재 위치와 선분 사이의 거리
      const dist = this.pointToSegmentDistance(this.x, this.y, p1.x, p1.y, p2.x, p2.y);

      if (dist < collisionRadius) {
        return true;
      }
    }

    return false;
  }

  /**
   * 점에서 선분까지의 최단 거리
   */
  private pointToSegmentDistance(
    px: number, py: number,
    x1: number, y1: number,
    x2: number, y2: number
  ): number {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const lengthSq = dx * dx + dy * dy;

    if (lengthSq === 0) {
      return Math.sqrt((px - x1) ** 2 + (py - y1) ** 2);
    }

    let t = ((px - x1) * dx + (py - y1) * dy) / lengthSq;
    t = Math.max(0, Math.min(1, t));

    const nearestX = x1 + t * dx;
    const nearestY = y1 + t * dy;

    return Math.sqrt((px - nearestX) ** 2 + (py - nearestY) ** 2);
  }

  /**
   * 영역으로 돌아왔을 때 호출 - 영역 확장
   */
  private completeTerritory(): void {
    if (!this.territoryManager || this.trail.length < 3) {
      this.resetTrail();
      return;
    }

    // 트레일 포인트로 영역 확장
    const trailPoints = this.trailRenderer.getPoints();

    const result = this.territoryManager.expandTerritory(this.id, trailPoints);

    if (result) {
      // 성공 효과
      this.particleManager?.confetti(this.x, this.y);

      // 영역 확장 브로드캐스트 콜백 호출
      if (this.onTerritoryCompleteCallback && trailPoints.length > 0) {
        this.onTerritoryCompleteCallback(trailPoints);
      }
    }

    this.resetTrail();
  }

  getGridPosition(): { x: number; y: number } {
    const { tileSize } = GAME_CONSTANTS.map;
    return {
      x: Math.floor(this.x / tileSize),
      y: Math.floor(this.y / tileSize),
    };
  }

  getTrailPoints(): { x: number; y: number }[] {
    return this.trailRenderer.getPoints();
  }

  isTrailActive(): boolean {
    return this.trailActive;
  }

  resetTrail(): void {
    this.trail = [];
    this.trailActive = false;
    this.trailRenderer.clear();
    this.territoryManager?.clearTrailRoot(this.id);
  }

  respawn(x: number, y: number): void {
    this.setPosition(x, y);
    this.resetTrail();
    this.respawnInvincible = true;
    this.isBoosting = false;
    this.isOutsideTerritory = false;
    this.effect.clear();

    this.scene.tweens.add({
      targets: this.cubeRenderer.getGraphics(),
      alpha: 0.3,
      duration: 200,
      yoyo: true,
      repeat: Math.floor(GAME_CONSTANTS.lives.respawnInvincibilityMs / 400),
      onComplete: () => {
        this.respawnInvincible = false;
        this.cubeRenderer.getGraphics().setAlpha(1);
      },
    });
  }

  /** 아이템 효과 적용 (감싸 먹기 발동 시) */
  applyEffect(type: ItemType): void {
    this.effect.apply(type, Date.now());
  }

  /** 스폰 보호용 일시 무적 (깜빡임 포함) — 리스폰 무적과 동일 메커니즘 */
  startInvincibility(durationMs: number): void {
    this.respawnInvincible = true;
    this.scene.tweens.add({
      targets: this.cubeRenderer.getGraphics(),
      alpha: 0.4,
      duration: 200,
      yoyo: true,
      repeat: Math.floor(durationMs / 400),
      onComplete: () => {
        this.respawnInvincible = false;
        this.cubeRenderer.getGraphics().setAlpha(1);
      },
    });
  }

  /** 효과에 따른 크기/꼬리굵기/무지개 시각 반영 (매 프레임 호출, 변화 없으면 저렴) */
  private applyEffectVisuals(): void {
    this.cubeRenderer.setSizeMultiplier(this.effect.sizeMultiplier());
    this.cubeRenderer.setRainbow(this.effect.isRainbow());
    this.trailRenderer.setWidth(this.cubeRenderer.getBaseWidth());
    this.trailRenderer.setRainbow(this.effect.isRainbow());
    this.trailRenderer.tickRainbow();
  }

  updateRemotePosition(x: number, y: number, direction: number): void {
    this.x = x;
    this.y = y;
    this.direction = direction;
    this.cubeRenderer.setDirection(direction);
  }

  getDirection(): number {
    return this.direction;
  }

  getColor(): number {
    return this.color;
  }

  getNickname(): string {
    return this.nickname;
  }

  setCrown(hasCrown: boolean): void {
    if (this.hasCrown === hasCrown) return;
    this.hasCrown = hasCrown;
    const displayText = hasCrown ? `👑 ${this.nickname}` : this.nickname;
    this.nicknameText.setText(displayText);
  }

  destroy(fromScene?: boolean): void {
    this.cubeRenderer.destroy();
    this.trailRenderer.destroy();
    this.nicknameText.destroy();
    this.effectTimerText.destroy();
    this.mobileControls?.destroy();
    super.destroy(fromScene);
  }
}
